import { Grid, Box } from '@mui/material'
import { Modal } from 'Components/Modal'
import { TextWithLabel } from 'Components/TextWithLabel'
import { SwitchWithLabel } from 'Components/Switch/SwitchWithLabel'
import { type ISD } from 'core/types'
import { useState } from 'react'
import { TextFieldWithLabel } from 'Components/TextField'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LabelWithToolTip } from 'Components/Label/Index'

interface ConfigurationEditProps {
  onClose: () => void
  show: boolean
  isdSettings: ISD
  onConfirm: (newIsdSettings: ISD) => void
}

interface MonthDayPickerProps {
  label: string
  toolTip: string
  value: Date | null
  onChange: (newValue: Date | null) => void
}

// a datepicker for selecting just a month and day
function MonthDayPicker (props: MonthDayPickerProps): JSX.Element {
  return <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      views={['month', 'day']}
      openTo="month"
      inputFormat="MM/DD"
      value={props.value}
      onChange={props.onChange}
      renderInput={({ inputRef, inputProps, InputProps }) => (
        <>
          <LabelWithToolTip labelText={props.label} toolTipText={props.toolTip} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <input style={{ padding: '16px', borderRadius: 4, border: '1px solid #ccc', fontSize: '16px' }} ref={inputRef} {...inputProps} />
            {InputProps?.endAdornment}
          </Box>
        </>
      )}
    />
  </LocalizationProvider>
}

export function ConfigurationEdit (props: ConfigurationEditProps): JSX.Element {
  const [isdSettings, setIsdSettings] = useState<ISD>(props.isdSettings)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    setIsdSettings({ ...isdSettings, [event.target.name]: checked })
  }

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setIsdSettings({ ...isdSettings, [event.target.name]: event.target.value })
  }

  const display = <>
    <Grid container>
      <Grid item xs={12}>
        <Box>
          <SwitchWithLabel
            onChange={handleChange}
            label='Capture IFSP'
            name='captureIFSP'
            checked={isdSettings.captureIFSP}
            toolTip='Capture ISFP'
          />
          <SwitchWithLabel
            onChange={handleChange}
            label='Capture IEP'
            name='captureIEP'
            checked={isdSettings.captureIEP}
            toolTip='Capture IEP'
          />
          <SwitchWithLabel
            onChange={handleChange}
            label='Capture EOIS'
            name='captureEOIS'
            checked={isdSettings.captureEOIS}
            toolTip='Capture EOIS'
          />
          <SwitchWithLabel
            onChange={handleChange}
            label='Show results from other counties than the resident district'
            name='showResultsFromOtherCounties'
            checked={isdSettings.showResultsFromOtherCounties}
            toolTip='Show results from other counties than the resident district'
          />
          <TextFieldWithLabel
            maxWidth='250px'
            onChange={handleTextChange}
            label='Maximum selectable facilities in caregiver portal'
            name='caregiverPortalMaxProviders'
            value={isdSettings.caregiverPortalMaxProviders}
            type='number'
          />
          <SwitchWithLabel
            onChange={handleChange}
            label='Show "Create a Referral"'
            name='showCreateReferral'
            checked={isdSettings.showCreateReferral}
          />
          <SwitchWithLabel
            onChange={handleChange}
            label='Show "Services for Myself"'
            name='showServicesForMyself'
            checked={isdSettings.showServicesForMyself}
          />

          <TextFieldWithLabel
            onChange={handleTextChange}
            label='Services for Myself Name'
            name='adultServicesName'
            value={isdSettings.adultServicesName ?? ''}
            toolTip='The name of the provider for adult services.'
          />
          <TextFieldWithLabel
            onChange={handleTextChange}
            label='Services for Myself Link'
            name='adultServicesLink'
            value={isdSettings.adultServicesLink ?? ''}
          />
          <MonthDayPicker
            label='Upcoming school-year rollover date'
            toolTip='The month and day for a given year when the school year rolls over'
            value={isdSettings.schoolYearRolloverDate}
            onChange={(newValue) => {
              if (newValue !== null) {
                setIsdSettings({ ...isdSettings, schoolYearRolloverDate: newValue })
              }
            }}
          />
          <MonthDayPicker
            label='Upcoming school-year enrollment opens'
            toolTip='The month and day each year from which caregivers can choose the upcoming school year or a specific date, until the rollover date. Leave empty to always offer them.'
            value={isdSettings.upcomingSchoolYearOpenDate}
            onChange={(newValue) => { setIsdSettings({ ...isdSettings, upcomingSchoolYearOpenDate: newValue }) }}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TextWithLabel label='Enrollment Process' value='' name='enrollmentProcess' />
        <Box>
          <SwitchWithLabel
            onChange={handleChange}
            label='Auto refer to Intake Provider regardless of selected providers'
            name='referAllToIntakeProvider'
            checked={isdSettings.referAllToIntakeProvider}
            toolTip='Auto refer to Intake Provider regardless of selected providers'
          />
          <SwitchWithLabel
            onChange={handleChange}
            label='Allow provider to select new provider on referral decline'
            name='selectProviderOnReferralDecline'
            checked={isdSettings.selectProviderOnReferralDecline}
            toolTip='Allow provider to select new provider on referral decline'
          />
        </Box>
      </Grid>
    </Grid>
  </>

  return (
    <Modal
      onClose={props.onClose}
      onConfirm={() => { props.onConfirm(isdSettings) }}
      open={props.show}
      confirmationContent={display}
    />
  )
}
